import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import type {
  MentorProfileChangeRequest,
  MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import { mentorProfileReviewRound } from "@/lib/mentor-profile/analytics";
import { PostHogEvents, useTrackPage } from "@/lib/posthog-events";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { DateTime } from "luxon";

interface PostApprovalHomeProps {
  draft: MentorProfileDraft;
  mentorId: string;
  reviewCreatedAt: string;
  changeRequests: MentorProfileChangeRequest[];
}

export function PostApprovalHome({
  draft,
  mentorId,
  reviewCreatedAt,
  changeRequests,
}: PostApprovalHomeProps) {
  const name = draft.display_name || draft.first_name || "there";
  const started = DateTime.fromISO(reviewCreatedAt);
  const totalDurationMs = started.isValid
    ? Math.max(0, DateTime.now().diff(started).as("milliseconds"))
    : undefined;

  useTrackPage(PostHogEvents.MENTOR_PROFILE_ONBOARDING_FLOW_COMPLETED, {
    mentor_id: mentorId,
    total_review_rounds: mentorProfileReviewRound(changeRequests),
    total_duration_ms: totalDurationMs,
  });

  return (
    <WizardBackground className="items-start justify-center gap-12">
      <div className="relative z-10 flex w-full max-w-[672px] flex-col">
        <div className="flex size-14 items-center justify-center rounded-full bg-[#22c55e] text-white">
          <Check className="size-7" strokeWidth={2.5} />
        </div>

        <h1 className="mt-5 font-heading text-[34px] font-bold leading-[1.05] tracking-[-0.85px] text-[#1f1e1d]">
          You&apos;re approved, {name}
        </h1>

        <p className="mt-3 font-body text-[15px] leading-[1.625] text-[#797774]">
          Your matching profile is live for teens. Next up is mentor training —
          once that&apos;s done you&apos;ll unlock the full dashboard.
        </p>

        <div className="mt-8 rounded-[22px] border border-[#d8f5e3] bg-[#f3fbf6] p-[21px]">
          <p className="font-body text-xs font-semibold uppercase tracking-[0.3px] text-[#797774]">
            What&apos;s next
          </p>
          <p className="mt-2 font-body text-[15px] font-medium text-[#1f1e1d]">
            Head into training to finish onboarding.
          </p>
          <Link
            to="/training"
            className="mt-4 inline-flex rounded-full bg-[#1f1e1d] px-5 py-2.5 font-heading text-sm font-bold text-white"
          >
            Go to training
          </Link>
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 flex-col items-center gap-3 self-start">
        <span className="text-xs text-[#797774]">
          Live preview · what teens see
        </span>
        <PhoneFrame>
          <MatchingProfilePreviewCard draft={draft} />
        </PhoneFrame>
      </div>
    </WizardBackground>
  );
}
